import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import {
  TOpsProgramNotes,
  updateOpsProgramNotes,
} from '../../../requests/opsRecords';
import { btnGhost, btnPrimary, btnSecondary, Card, inputClass } from '../ui';

/** The notes cascade: the program's note, shown (and editable) on every class in it. */
const ProgramNoteCard: React.FC<{ programNotes: TOpsProgramNotes }> = ({
  programNotes,
}) => {
  const queryClient = useQueryClient();
  const [editingProgramNote, setEditingProgramNote] = useState(false);
  const [programNoteDraft, setProgramNoteDraft] = useState('');
  const [programNoteVisible, setProgramNoteVisible] = useState(true);
  const saveProgramNote = useMutation({
    mutationFn: () =>
      updateOpsProgramNotes(programNotes!.programId, {
        info: programNoteDraft,
        visible: programNoteVisible,
      }),
    onSuccess: () => {
      showToast(
        'Program note saved — every class in the program shows it',
        'success',
      );
      setEditingProgramNote(false);
      queryClient.invalidateQueries({ queryKey: ['opsClassProgramNotes'] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  return (
    <Card
      title={`From the program · ${programNotes.programName ?? ''}`}
      right={
        !editingProgramNote ? (
          <button
            type="button"
            className={`${btnGhost} text-xs`}
            onClick={() => {
              setProgramNoteDraft(programNotes.info ?? '');
              setProgramNoteVisible(programNotes.visible);
              setEditingProgramNote(true);
            }}
          >
            edit program note
          </button>
        ) : undefined
      }
    >
      <div className="p-5 text-sm">
        {editingProgramNote ? (
          <div className="grid gap-3">
            <textarea
              className={`${inputClass} w-full`}
              rows={6}
              value={programNoteDraft}
              onChange={(e) => setProgramNoteDraft(e.target.value)}
              placeholder="Program relevant information — shown on every class in this program"
            />
            <label className="flex items-center gap-2 text-sm text-gray-five">
              <input
                type="checkbox"
                className="ops-check h-4 w-4 accent-blue-primary"
                checked={programNoteVisible}
                onChange={(e) => setProgramNoteVisible(e.target.checked)}
              />
              Visible on classes and the trainer view
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                className={btnPrimary}
                disabled={saveProgramNote.isPending}
                onClick={() => saveProgramNote.mutate()}
              >
                {saveProgramNote.isPending ? 'Saving…' : 'Save program note'}
              </button>
              <button
                type="button"
                className={btnSecondary}
                onClick={() => setEditingProgramNote(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : programNotes.visible && programNotes.info ? (
          <div className="whitespace-pre-wrap text-gray-five">
            {programNotes.info}
          </div>
        ) : (
          <div className="text-gray-bold">
            {programNotes.info
              ? 'The program has a note, but it is marked not visible — classes and the trainer view do not show it.'
              : 'No program note yet. Write it once here and every class in the program carries it.'}
          </div>
        )}
        {!editingProgramNote && (
          <div className="mt-2 text-xs text-gray-two">
            Read-only on the class — it belongs to the program
            {programNotes.via === 'opportunity' ? ' (via the Opportunity)' : ''}
            .
          </div>
        )}
      </div>
    </Card>
  );
};

export default ProgramNoteCard;
